import React, { useContext, useEffect, useMemo, useState } from "react";
import styles from "./css/subscription.module.css";
import capStyles from "./css/Racapnotice.module.css";
import Button from "common-ui-components/components/button/Button";
import EM_API from "../../../../common-ui-components/components/utils/API";
import { useLocation } from "react-router-dom";
import { Route, Routes } from "react-router-dom";
import Manage from "./manage-subscription/Manage";
import { UserDetailsContext } from "App";
import InfoIcon from "common-ui-components/info-icon/InfoIcon";
import PageRestrict from "../../../../common-ui-components/components/restrictionUI/page/PageRestrict";
import { getFeatureMap } from "./featureMap";
import {
  IS_MSP,
  PRODUCT_CONTEXT,
} from "common-ui-components/components/utils/Config";
import { useRestrictionChecker } from "common-ui-components/components/restrictionUI/RestrictionChecker";
import SubscriptionLicense from "./SubscriptionLicense";
import CompareFeatures from "./features/CompareFeatures";
import { getCompareFeaturesData } from "./features/compareFeaturesData";
import Select from "common-ui-components/components/select";
import lockIcon from "common-ui-components/components/icons/lock.svg";
import {
  sortEditions,
  computeSlabTotal,
  getConversionRate,
} from "./utils/Pricingmath";
import { getFallbackPricingCatalog } from "./utils/Pricingfallbackcatalog";
import { CURRENCY_SYMBOLS } from "./utils/Currencysymbols";
import { WarningIcon, HelpIcon } from "./utils/icons";
import { normalizePricingCatalog } from "./utils/normalizePricingCatalog";
import SubscriptionSkeleton from "./SubscriptionSkeleton";
import { ArrowLeft } from "react-feather";

// PRODUCT_CONTEXT never changes at runtime, so it's safe to branch render output/derived values on it.
// Never use it to conditionally call a hook - every hook below still runs unconditionally every render.
const IS_RA = PRODUCT_CONTEXT === "ra";

const DEFAULT_DEVICE_COUNT = IS_RA ? 25 : 50;
const MIN_DEVICE_COUNT = IS_RA ? 25 : 50;

// Only used if payment/pricing-catalog is unreachable or empty - see pricing/pricingFallbackCatalog.js.
const FALLBACK_PRICING_CATALOG = getFallbackPricingCatalog(IS_RA);

const viewKey = "SUBSCRIPTIONS_VIEW";
const manageKey = "SUBSCRIPTIONS_MANAGE";

function Subscription({
  endpoints = DEFAULT_DEVICE_COUNT,
  onBack,
  popup = false,
}) {
  const product = PRODUCT_CONTEXT;
  const featureMap = getFeatureMap(product);

  const location = useLocation();
  const userDetails = useContext(UserDetailsContext);
  const role = userDetails.roleName;

  const { checkRestriction } = useRestrictionChecker();
  const canView = !checkRestriction(viewKey, false, "page");

  const [subscriptionData, setSubscriptionData] = useState(null);
  const [btnLoading, setBtnLoading] = useState(false);
  const [technicianCount, setTechnicianCount] = useState(0);
  const [deviceCount, setDeviceCount] = useState(
    endpoints || DEFAULT_DEVICE_COUNT,
  );
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(0);
  // Tracks whether the user has explicitly picked an edition. Until they do, we auto-select
  // Enterprise (falling back to Professional) whenever the plan list loads/changes.
  const [userSelectedPlan, setUserSelectedPlan] = useState(false);
  const [isYearly, setIsYearly] = useState(true);
  const [pricingCatalog, setPricingCatalog] = useState(null);
  const [currencyCode, setCurrencyCode] = useState("USD");
  const [registeredPlans, setRegisteredPlans] = useState(false);
  const [addons, setAddons] = useState({
    storage: false,
    premiumSupport: false,
  });
  const [storageGb, setStorageGb] = useState(10);

  const [loading, setLoading] = useState(false);
  const [isLicenseLoading, setIsLicenseLoading] = useState(false);
  const [block, setBlock] = useState(false);
  const [restrict, setRestrict] = useState(false);

  // Technicians can't self-serve a subscription.
  useEffect(() => {
    if (role === "Technician") setRestrict(true);
  }, [role]);

  // Existing license, if any - drives the early-return license view below.
  useEffect(() => {
    const fetchSubscriptionLicense = async () => {
      try {
        setIsLicenseLoading(true);
        const response = await EM_API.get("/payment/current-plan");
        if (response.status.code === 200) setSubscriptionData(response.body);
      } catch (error) {
        console.error("Error fetching subscription license data:", error);
      } finally {
        setIsLicenseLoading(false);
      }
    };
    if (canView) fetchSubscriptionLicense();
  }, [canView]);

  // 409 = already registered, 412 = no catalog for this account, anything else missing data = fall back to static pricing.
  useEffect(() => {
    const fetchPricingCatalog = async () => {
      setLoading(true);
      try {
        const response = await EM_API.get("payment/pricing-catalog");

        if (response.status.code === 409) {
          setRegisteredPlans(true);
          return;
        }
        if (response.status.code === 412) {
          setPricingCatalog(null);
          return;
        }

        const body = normalizePricingCatalog(response.body);
        if (body?.devicePricingByEdition?.length > 0) {
          setPricingCatalog(body);
          setCurrencyCode(body.orgCurrencyCode || "USD");
        } else {
          setPricingCatalog(FALLBACK_PRICING_CATALOG);
          setCurrencyCode(FALLBACK_PRICING_CATALOG.orgCurrencyCode);
          setBlock(true);
        }
      } catch (error) {
        console.error("Error fetching pricing catalog:", error);
        setPricingCatalog(FALLBACK_PRICING_CATALOG);
        setCurrencyCode(FALLBACK_PRICING_CATALOG.orgCurrencyCode);
        setBlock(true);
      } finally {
        setLoading(false);
      }
    };

    if (canView) fetchPricingCatalog();
  }, [canView]);

  // Org currency + whatever target currencies the catalog has conversion rates for. No API call, just re-reads the catalog.
  const currencyOptions = useMemo(() => {
    if (!pricingCatalog) return [];

    const codes = [
      "USD",
      pricingCatalog.orgCurrencyCode,
      ...(pricingCatalog.currencies || []).map((c) => c.targetCurrencyCode),
    ];

    const uniqueCodes = [...new Set(codes)];

    return uniqueCodes.map((code) => ({
      value: code,
      label: `${CURRENCY_SYMBOLS[code] || code} ${code}`,
    }));
  }, [pricingCatalog]);

  // One row per edition, priced client-side against pricingCatalog for the current device/technician/currency/period.
  // Device and technician prices are slab-interpolated (see computeSlabTotal), not a flat per-bracket rate.
  const displayPlans = useMemo(() => {
    if (!pricingCatalog?.devicePricingByEdition?.length) return [];
    const period = isYearly ? "Annually" : "Monthly";
    const conversionRate = getConversionRate(pricingCatalog, currencyCode);

    const technicianMonthlyTotal =
      computeSlabTotal(
        technicianCount,
        pricingCatalog.technicianPricing,
        "minTechnician",
        "maxTechnician",
        "monthlyUnitPrice",
      ) * conversionRate;
    const technicianYearlyTotal =
      computeSlabTotal(
        technicianCount,
        pricingCatalog.technicianPricing,
        "minTechnician",
        "maxTechnician",
        "yearlyUnitPrice",
      ) * conversionRate;

    return sortEditions(pricingCatalog.devicePricingByEdition).map(
      (edition) => {
        const monthlyDeviceTotal =
          computeSlabTotal(
            deviceCount,
            edition.pricingRanges,
            "minDevices",
            "maxDevices",
            "monthlyUnitPrice",
          ) * conversionRate;
        const yearlyDeviceOriginal = monthlyDeviceTotal * 12;
        const yearlyDeviceDiscounted =
          computeSlabTotal(
            deviceCount,
            edition.pricingRanges,
            "minDevices",
            "maxDevices",
            "yearlyUnitPrice",
          ) * conversionRate;
        const savedAmount = yearlyDeviceOriginal - yearlyDeviceDiscounted;
        const discountPercentage = yearlyDeviceOriginal
          ? (savedAmount / yearlyDeviceOriginal) * 100
          : 0;

        const devicePrice = isYearly
          ? yearlyDeviceDiscounted
          : monthlyDeviceTotal;
        const technicianPrice = isYearly
          ? technicianYearlyTotal
          : technicianMonthlyTotal;
        const originalPrice = isYearly
          ? yearlyDeviceOriginal
          : monthlyDeviceTotal;

        const plan = edition.plans?.find((p) => p.period === period);
        const planKey = `${edition.editionName} ${period}`;

        return {
          planId: plan?.planId,
          planName: edition.editionName,
          period,
          devicePrice: Number(devicePrice.toFixed(2)),
          technicianPrice: Number(technicianPrice.toFixed(2)),
          // Monthly-rate totals kept regardless of period, so the discount comparison below can build a true
          // "monthly x12" figure including technicians/add-ons, not just the device price.
          monthlyDevicePrice: Number(monthlyDeviceTotal.toFixed(2)),
          monthlyTechnicianPrice: Number(technicianMonthlyTotal.toFixed(2)),
          originalPrice: Number(originalPrice.toFixed(2)),
          savedAmount: Number(savedAmount.toFixed(2)),
          discountPercentage: Number(discountPercentage.toFixed(3)),
          currencyCode,
          conversionRate,
          includes: featureMap[planKey]?.includes || "",
          features: featureMap[planKey]?.features || [],
        };
      },
    );
  }, [
    pricingCatalog,
    isYearly,
    currencyCode,
    deviceCount,
    technicianCount,
    featureMap,
  ]);

  // Default edition selection: Enterprise if it exists, otherwise Professional, otherwise the first plan.
  // Only runs automatically until the user explicitly picks an edition (userSelectedPlan flips to true).
  useEffect(() => {
    if (!displayPlans.length) return;

    if (!userSelectedPlan) {
      const enterpriseIndex = displayPlans.findIndex(
        (plan) => plan.planName === "Enterprise",
      );
      const professionalIndex = displayPlans.findIndex(
        (plan) => plan.planName === "Professional",
      );
      const defaultIndex =
        enterpriseIndex !== -1
          ? enterpriseIndex
          : professionalIndex !== -1
            ? professionalIndex
            : 0;

      setSelectedPlanIndex(defaultIndex);
      return;
    }

    if (selectedPlanIndex >= displayPlans.length) setSelectedPlanIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayPlans, userSelectedPlan]);

  // Scroll to #compare-plans-and-features once Content (and CompareFeatures inside it)
  // is actually what's rendering — mirrors indexElement's own conditions.
  useEffect(() => {
    if (!location.hash) return;
    if (isLicenseLoading || loading) return;
    if (subscriptionData || registeredPlans || restrict) return;

    const id = location.hash.slice(1);
    const raf = requestAnimationFrame(() => {
      document
        .getElementById(id)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(raf);
  }, [
    location.hash,
    isLicenseLoading,
    loading,
    subscriptionData,
    registeredPlans,
    restrict,
    displayPlans,
  ]);

  const currentPlan = displayPlans[selectedPlanIndex] || {};

  // Looked up by addonName since these also double as the "does this addon exist for this product" check -
  // if the catalog doesn't carry it, its UI is hidden entirely rather than showing an unpriceable toggle.
  const storageAddon = useMemo(
    () =>
      pricingCatalog?.addons?.find(
        (a) => a.addonName === "Additional Storage",
      ) || null,
    [pricingCatalog],
  );
  const supportAddon = useMemo(
    () =>
      pricingCatalog?.addons?.find((a) => a.addonName === "Premium Support") ||
      null,
    [pricingCatalog],
  );

  const isStandardCapped = false;

  const professionalPlanIndex = displayPlans.findIndex(
    (plan) => plan.planName === "Professional",
  );

  const handleSwitchToProfessional = () => {
    if (professionalPlanIndex !== -1) {
      setUserSelectedPlan(true);
      setSelectedPlanIndex(professionalPlanIndex);
    }
  };

  // amount == null (not !amount) so a genuine 0 still renders "$0.00" instead of an empty string.
  const formatPrice = (amount, code) => {
    if (amount == null || !code) return "";
    const symbol = CURRENCY_SYMBOLS[code] || code;
    const number = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
    return `${symbol}${number}`;
  };

  // Storage + premium support rates come from pricingCatalog.addons, computed client-side - toggling never calls the API.
  // Premium support's basis differs by product: RA charges 20% of device+technician combined (one "PLAN" line in the
  // summary); everyone else charges 20% of device price only (technicians get their own line). totalPrice is the same
  // formula either way - device + technician + storage + support.
  const priceBreakdown = useMemo(() => {
    if (!currentPlan.planId || !pricingCatalog) return null;
    const conversionRate = currentPlan.conversionRate || 1;

    const supportPercentage = (supportAddon?.percentage || 0) / 100;

    const storageUnitPrice = isYearly
      ? storageAddon?.yearlyUnitPrice || 0
      : storageAddon?.monthlyUnitPrice || 0;
    const storagePrice =
      addons.storage && storageAddon
        ? storageUnitPrice * storageGb * conversionRate
        : 0;

    const supportBasisPrice = IS_RA
      ? currentPlan.devicePrice + currentPlan.technicianPrice
      : currentPlan.devicePrice;
    const premiumSupportPrice =
      addons.premiumSupport && supportAddon
        ? supportBasisPrice * supportPercentage
        : 0;

    const totalPrice =
      currentPlan.devicePrice +
      currentPlan.technicianPrice +
      storagePrice +
      premiumSupportPrice;

    // "What this would've cost paying monthly for 12 months" - same line items as totalPrice, at the monthly rate.
    let originalAnnualTotal = null;
    let annualSavedAmount = 0;
    let annualDiscountPercentage = 0;
    if (isYearly) {
      const monthlyDevicePrice = currentPlan.monthlyDevicePrice || 0;
      const monthlyTechnicianPrice = currentPlan.monthlyTechnicianPrice || 0;
      const monthlyStoragePrice =
        addons.storage && storageAddon
          ? (storageAddon?.monthlyUnitPrice || 0) * storageGb * conversionRate
          : 0;
      const monthlySupportBasisPrice = IS_RA
        ? monthlyDevicePrice + monthlyTechnicianPrice
        : monthlyDevicePrice;
      const monthlyPremiumSupportPrice =
        addons.premiumSupport && supportAddon
          ? monthlySupportBasisPrice * supportPercentage
          : 0;

      originalAnnualTotal =
        (monthlyDevicePrice +
          monthlyTechnicianPrice +
          monthlyStoragePrice +
          monthlyPremiumSupportPrice) *
        12;
      annualSavedAmount = originalAnnualTotal - totalPrice;
      annualDiscountPercentage = originalAnnualTotal
        ? (annualSavedAmount / originalAnnualTotal) * 100
        : 0;
    }

    return {
      planPrice: currentPlan.devicePrice,
      technicianPrice: currentPlan.technicianPrice,
      storagePrice,
      premiumSupportPrice,
      totalPrice,
      originalAnnualTotal,
      savedAmount: annualSavedAmount,
      discountPercentage: annualDiscountPercentage,
    };
  }, [
    currentPlan,
    addons.storage,
    addons.premiumSupport,
    storageGb,
    isYearly,
    pricingCatalog,
    storageAddon,
    supportAddon,
  ]);

  const discountInfo =
    isYearly && !isStandardCapped && priceBreakdown?.savedAmount > 0
      ? {
          originalAnnual: priceBreakdown.originalAnnualTotal,
          saved: priceBreakdown.savedAmount,
          discountPercent: Math.round(priceBreakdown.discountPercentage || 0),
        }
      : null;

  const handleToggle = (val) => {
    if (IS_RA && !val) return;
    setIsYearly(val);
  };

  // Minimum endpoints allowed: 100 for RA, 50 for everything else - matches the input's `min` attribute below,
  // so the up/down spinner arrows and manual typing agree on the floor.
  const handleDeviceCountChange = (e) => {
    const val = parseInt(e.target.value, 10);

    if (isNaN(val)) {
      setDeviceCount(MIN_DEVICE_COUNT);
      return;
    }

    const clampedValue = Math.max(MIN_DEVICE_COUNT, Math.min(10000, val));
    setDeviceCount(clampedValue);
  };

  const handleDeviceCountBlur = () => {};

  // Additional technicians can go down to 0 (e.g. RA Standard's cap), so both the clamp here and the
  // input's `min` attribute below allow 0.
  const handleTechnicianCountChange = (e) => {
    const val = parseInt(e.target.value, 10);
    setTechnicianCount(isNaN(val) ? 0 : Math.max(0, Math.min(50, val)));
  };

  const handleStorageGbChange = (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setStorageGb("");
      return;
    }
    const val = parseInt(raw, 10);
    setStorageGb(isNaN(val) ? 0 : Math.max(0, Math.min(50, val)));
  };

  const handleStorageGbBlur = () => {
    setStorageGb((prev) => {
      if (prev === "" || prev < 1) return 1;
      if (prev > 1000) return 1000;
      return prev;
    });
  };

  // Matches the backend's order-creation contract: { planId, deviceCount, technicianCount, currencyCode, addonDTO }.
  // addonDTO carries `count` for unit-priced addons (storage) but omits it for percentage-based ones (premium support) -
  // the backend derives that count itself. Only included if selected AND actually present in the catalog.
  const buildAddonDTO = () => {
    const addonDTO = [];
    if (addons.storage && storageAddon)
      addonDTO.push({
        addonId: storageAddon.addonId,
        addonName: storageAddon.addonName,
        count: storageGb,
      });
    if (addons.premiumSupport && supportAddon)
      addonDTO.push({
        addonId: supportAddon.addonId,
        addonName: supportAddon.addonName,
      });
    return addonDTO;
  };

  const handleProceedToPay = async () => {
    if (isStandardCapped) return;
    if (checkRestriction(manageKey)) return;

    const orderPayload = {
      planId: currentPlan.planId,
      deviceCount,
      technicianCount,
      currencyCode,
      addonDTO: buildAddonDTO(),
    };

    const plan = {
      ...currentPlan,
      deviceCount,
      technicianCount,
      currencyCode,
      priceBreakdown,
      addons: {
        ...addons,
        storageGb: addons.storage ? storageGb : null,
      },
      orderPayload,
    };

    setBtnLoading(true);
    try {
      const response = await EM_API.post(
        "/payment/create-checkout",
        orderPayload,
      );

      if (response.status.success && response.body) {
        const openChargebeeCheckout = () => {
          const cbInstance = window.Chargebee.getInstance();

          cbInstance.openCheckout({
            hostedPage: () => Promise.resolve(response.body),
          });
        };

        openChargebeeCheckout();
      } else {
        alert(response.status.message);
      }
    } catch (err) {
      console.error("Checkout creation failed:", err);
      alert("An error occurred while proceeding to pay.");
    } finally {
      setBtnLoading(false);
    }
  };

  const toggleAddon = (key) =>
    setAddons((prev) => ({ ...prev, [key]: !prev[key] }));

  const activeCurrencyIndex = useMemo(() => {
    const idx = currencyOptions.findIndex((o) => o.value === currencyCode);
    return idx === -1 ? 0 : idx;
  }, [currencyOptions, currencyCode]);

  let productTitle = "Zecurit Endpoint Manager";

  switch (product) {
    case "ra":
      productTitle = "Zecurit Remote Access";
      break;
    case "am":
      productTitle = "Zecurit Asset Manager";
      break;
    case "vm":
      productTitle = "Zecurit Vulnarability Manager";
      break;
    case "pm":
      productTitle="Zecurit Patch Manager"
      break;
    default:
      productTitle = "Zecurit Endpoint Manager";
  }

  if (IS_MSP) productTitle = "Zecurit Endpoint Manager MSP";
  const deviceHint = `Minimum ${IS_RA ? 25 : 50} endpoints`;
  const planMeta = `${deviceCount} Endpoints`;
  const premiumSupportMeta = addons.premiumSupport
    ? "20% of endpoint cost"
    : "Not Selected";

  const Content = (
    <div className={styles.emPageWrapper}>
      <div className={styles.emPageHeader}>
        <h1 className={styles.emPageTitle}>Subscriptions</h1>
        <div className={styles.currencySelectWrapper}>
          <Select
            options={currencyOptions}
            active={activeCurrencyIndex}
            setActive={() => {}}
            onOptionSelect={(option) => setCurrencyCode(option.value)}
            triggerProps={{ className: styles.currencySelect }}
            aria-label="Currency"
          />
        </div>
      </div>

      <div className={styles.emParent}>
        <div className={styles.emLeftCol}>
          <div className={styles.emPlanCard}>
            <div className={styles.emCardHeader}>
              <h2 className={styles.emCardTitle}>{productTitle}</h2>
              <div className={styles.emBillingToggle}>
                <span className={styles.saveBadge}>Save 17% yearly</span>
                <div className={styles.pillToggle}>
                  <button
                    type="button"
                    className={!isYearly ? styles.pillActive : styles.pill}
                    onClick={() => handleToggle(false)}
                    disabled={IS_RA}
                    title={
                      IS_RA ? "Only yearly billing is available" : undefined
                    }
                    style={IS_RA ? { cursor: "not-allowed" } : undefined}
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    className={isYearly ? styles.pillActive : styles.pill}
                    onClick={() => handleToggle(true)}
                  >
                    Yearly
                  </button>
                </div>
              </div>
            </div>

            <div className={styles.emRow}>
              <div className={styles.emRowLabel}>
                Edition
                <InfoIcon content="Choose the edition that fits your team." />
              </div>
              <div className={styles.emRowInput}>
                <div className={styles.editionOptions}>
                  {displayPlans.map((plan, index) => (
                    <label key={plan.planName} className={styles.radioOption}>
                      <input
                        type="radio"
                        name="edition"
                        checked={selectedPlanIndex === index}
                        onChange={() => {
                          setUserSelectedPlan(true);
                          setSelectedPlanIndex(index);
                        }}
                      />
                      <span>{plan.planName}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.emRow}>
              <div className={styles.emRowLabel}>Endpoints</div>
              <div className={styles.emRowInput}>
                <input
                  type="number"
                  className={styles.numberInput}
                  value={deviceCount}
                  onChange={handleDeviceCountChange}
                  onBlur={handleDeviceCountBlur}
                  onKeyDown={(e) => {
                    if (["e", "E", "+", "-", "."].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  min={MIN_DEVICE_COUNT}
                  max={10000}
                />
                <span className={styles.emHint}>{deviceHint}</span>
              </div>
            </div>

            <div className={styles.emRow}>
              <div className={styles.emRowLabel}>Additional Technicians</div>
              <div className={styles.emRowInput}>
                <input
                  type="number"
                  className={styles.numberInput}
                  value={technicianCount}
                  onChange={handleTechnicianCountChange}
                  onFocus={(e) => e.target.select()}
                  onKeyDown={(e) => {
                    if (["e", "E", "+", "-", "."].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  min={0}
                  max={50}
                />
                <span className={styles.emHint}>
                  Optional technical support staff
                </span>
              </div>
            </div>

            {isStandardCapped && (
              <div className={capStyles.capNotice}>
                <div className={capStyles.capNoticeMain}>
                  <WarningIcon className={capStyles.capNoticeIcon} />
                  <p className={capStyles.capNoticeText}>
                    <span className={capStyles.capNoticeStrong}>
                      Standard Edition is capped at 100 devices and 1
                      technician.
                    </span>{" "}
                    To add more devices or technicians, switch to Professional
                    Edition.
                  </p>
                </div>
                <Button
                  type="button"
                  className={capStyles.capNoticeButton}
                  onClick={handleSwitchToProfessional}
                >
                  Switch to Professional
                </Button>
              </div>
            )}
          </div>

          {(storageAddon || supportAddon) && (
            <div className={styles.emAddonsCard}>
              <p className={styles.emCardSubtitle}>Add-ons</p>

              {storageAddon && (
                <div className={styles.addonItem}>
                  <label className={styles.checkboxOption}>
                    <input
                      type="checkbox"
                      checked={addons.storage}
                      onChange={() => toggleAddon("storage")}
                    />
                    <div>
                      <p className={styles.checkboxTitle}>Additional Storage</p>
                      <p className={styles.checkboxDesc}>
                        Extra storage for remote access session recordings
                      </p>
                    </div>
                  </label>

                  {addons.storage && (
                    <div className={styles.addonExpansion}>
                      <span className={styles.addonExpansionLabel}>
                        Storage (GB)
                      </span>
                      <input
                        type="number"
                        className={styles.addonExpansionInput}
                        value={storageGb === 0 ? "" : storageGb}
                        onChange={handleStorageGbChange}
                        onBlur={handleStorageGbBlur}
                        min={1}
                        max={50}
                        onKeyDown={(e) => {
                          if (["e", "E", "+", "-", "."].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              )}

              {supportAddon && (
                <div className={styles.addonItem}>
                  <label className={styles.checkboxOption}>
                    <input
                      type="checkbox"
                      checked={addons.premiumSupport}
                      onChange={() => toggleAddon("premiumSupport")}
                    />
                    <div>
                      <div className="flex">
                        <p className={styles.checkboxTitle}>Premium Support</p>
                        <InfoIcon content="Get a dedicated account manager and priority response times." />
                      </div>
                      <p className={styles.checkboxDesc}>
                        Priority support access with dedicated account manager
                      </p>
                    </div>
                  </label>
                </div>
              )}
            </div>
          )}
        </div>

        <div className={styles.emPaymentCard}>
          <div className={styles.emCardHeader}>
            <h2 className={styles.emCardTitle}>Payment Details</h2>
            <span className={styles.billingBadge}>
              {isYearly ? "Yearly" : "Monthly"}
            </span>
          </div>

          <p className={styles.emPlanLabel}>PLAN</p>
          <div className={styles.emPlanRow}>
            <div>
              <p className={styles.emPlanName}>
                {currentPlan.planName} Edition
              </p>
              <p className={styles.emPlanMeta}>{planMeta}</p>
            </div>
            <p className={styles.emPlanPrice}>
              {isStandardCapped ? (
                <span className={capStyles.dash}>—</span>
              ) : (
                formatPrice(priceBreakdown?.planPrice ?? 0, currencyCode)
              )}
            </p>
          </div>

          {/* RA folds technicians into the PLAN row above; every other product breaks them out here. */}
          <div className={styles.emPlanRow}>
            <div>
              <p className={styles.emPlanName}>Additional Technicians</p>
              <p className={styles.emPlanMeta}>
                {technicianCount == 0
                  ? "Not Selected"
                  : `${technicianCount} technicians`}
              </p>
            </div>
            <p className={styles.emPlanPrice}>
              {formatPrice(priceBreakdown?.technicianPrice ?? 0, currencyCode)}
            </p>
          </div>

          {(addons.storage || addons.premiumSupport) && (
            <p className={styles.emPlanLabel}>ADD-ONS</p>
          )}

          {addons.storage && storageAddon && (
            <div className={styles.emPlanRow}>
              <div>
                <p className={styles.emPlanName}>Additional Storage</p>
                <p className={styles.emPlanMeta}>{storageGb} GB</p>
              </div>
              <p className={styles.emPlanPrice}>
                {formatPrice(priceBreakdown?.storagePrice ?? 0, currencyCode)}
              </p>
            </div>
          )}

          {addons.premiumSupport && supportAddon && !isStandardCapped && (
            <div className={styles.emPlanRow}>
              <div>
                <p className={styles.emPlanName}>Premium Support</p>
                <p className={styles.emPlanMeta}>{premiumSupportMeta}</p>
              </div>
              <p className={styles.emPlanPrice}>
                {formatPrice(
                  priceBreakdown?.premiumSupportPrice ?? 0,
                  currencyCode,
                )}
              </p>
            </div>
          )}

          <div className={styles.emTotalRow}>
            <p className={styles.emTotalLabel}>Total Amount</p>
            <p className={styles.emTotalPrice}>
              {isStandardCapped ? (
                <span className={capStyles.dash}>—</span>
              ) : (
                formatPrice(priceBreakdown?.totalPrice ?? 0, currencyCode)
              )}
            </p>
          </div>
          <div style={{ margin: 0 }} className={styles.emDiscountBlock}>
            <p
              className={styles.emDiscountRow}
              style={{ opacity: discountInfo ? 1 : 0 }}
            >
              <span className={styles.emDiscountPercent}>
                Discount {discountInfo ? discountInfo.discountPercent : 0}%
              </span>
              <span className={styles.emOriginalPrice}>
                {discountInfo
                  ? formatPrice(discountInfo.originalAnnual, currencyCode)
                  : "$0.00"}
              </span>
            </p>
          </div>

          <Button
            variant="primary"
            className={styles.emPayButton}
            onClick={handleProceedToPay}
            disabled={block || isStandardCapped}
            loading={btnLoading}
          >
            Proceed to Pay
          </Button>
          <a
            className={styles.emQuoteLink}
            href="https://zecurit.com/get-quote"
            target="_blank"
            rel="noopener noreferrer"
          >
            Prefer a custom quote? Get a Quote
          </a>
          <p className={styles.emSecureNote}>
            <img src={lockIcon} alt="Lock" className={styles.emSecureIcon} />
            Secure payment gateway
          </p>

          <div className={styles.emHelpBox}>
            <HelpIcon className={styles.emHelpIcon} />
            <div className={styles.emHelpBody}>
              <p className={styles.emHelpTitle}>How can we help?</p>
              <p className={styles.emHelpDesc}>
                Questions?{" "}
                <a href="mailto:sales@zecurit.com">Contact sales@zecurit.com</a>
              </p>
            </div>
          </div>
        </div>
      </div>

      <CompareFeatures
        data={getCompareFeaturesData()}
        plans={displayPlans}
        selectedPlanIndex={selectedPlanIndex}
        onSelectPlan={setSelectedPlanIndex}
        product={product}
      />
    </div>
  );

  const indexElement = isLicenseLoading ? (
    <SubscriptionSkeleton />
  ) : subscriptionData ? (
    <SubscriptionLicense subscriptionData={subscriptionData} />
  ) : registeredPlans ? (
    <p className={styles.registered}>You are already registered</p>
  ) : loading ? (
    <SubscriptionSkeleton />
  ) : restrict ? (
    <PageRestrict />
  ) : (
    Content
  );

  const routes = (
    <Routes>
      <Route index element={indexElement} />
      <Route path="manage" element={<Manage />} />
      <Route path="*" element={indexElement} />
    </Routes>
  );

  if (!onBack) return routes;

  return (
    <div
      className={popup ? styles.expiredTrialPopup : styles.expiredTrialViewport}
    >
      <div className={styles.expiredTrialBackRow}>
        <button
          type="button"
          className={styles.expiredTrialBackButton}
          onClick={onBack}
        >
          <ArrowLeft size={16} aria-hidden="true" />
          Back
        </button>
      </div>
      {indexElement}
    </div>
  );
}

export default Subscription;
